import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import {
	Battery,
	Zap,
	Cpu,
	Volume2,
	Usb,
	Smartphone,
	Wifi,
} from "lucide-react";
import ContactBlock from "../../components/contact/ContactBlock";
import { Link } from "react-router-dom";

const MotorizedSmart = () => {
	const bounceTransition = {
		duration: 0.4,
		ease: [0.68, -0.25, 0.265, 1.25],
	};

	const features = [
		{
			icon: <Volume2 className="w-8 h-8 text-primary" />,
			title: "Quiet Operation",
			description: "Whisper quiet, 32 decibel operating noise.",
			note: "* Noise levels have been measured in accordance with EN ISO 3745.",
		},
		{
			icon: <Zap className="w-8 h-8 text-primary" />,
			title: "Powerful",
			description:
				"Compact yet powerful motors can handle large and small window shades.",
		},
		{
			icon: <Cpu className="w-8 h-8 text-primary" />,
			title: "Smart",
			description:
				"Rechargeable motors feature built-in communication for cutting-edge integration. The Connector Hub enables communication with WiFi based systems.",
		},
	];

	const benefits = [
		{
			icon: <Battery className="w-12 h-12 text-primary" />,
			title: "Battery Powered",
			description:
				"Built-in rechargeable battery lasts up to 6 months, making maintenance a breeze.",
		},
		{
			icon: <Usb className="w-12 h-12 text-primary" />,
			title: "USB-C Standard",
			description:
				"Recharge with the convenience of a USB Type-C Charging cable. A full charge can take up to X hours and last up to 6 months!",
		},
		{
			icon: <Wifi className="w-12 h-12 text-primary" />,
			title: "Control Options",
			description:
				"Remotes have up to 16 channels plus 6 extra custom groups, giving you ample options to command your devices",
		},
		{
			icon: <Smartphone className="w-12 h-12 text-primary" />,
			title: "Smart Life App",
			description:
				"This free app helps you to take control of your home! Set the perfect scene, schedule timers, operate your shades, and even use voice control through other devices",
		},
	];

	return (
		<div className="overflow-x-hidden bg-gradient-to-b from-white to-gray-50">
			<Helmet>
				<title>
					Automated Motorized Roller Shades: Best Automated Motorized Window
					Shades | Off Cut Shades
				</title>
				<meta
					name="description"
					content="Best automated shades in Canada with Off Cut Shades. Providing custom automated shades & blinds for windows at low prices in the most popular fabrics."
				/>
				<link
					rel="canonical"
					href="https://offcutshades.com/motorized-solutions"
				/>
			</Helmet>

			<main>
				{/* Hero Section */}
				<section className="relative pt-12 overflow-hidden">
					<motion.div
						className="container mx-auto px-4"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={bounceTransition}
					>
						<motion.h1
							className="relative z-10 text-5xl md:text-7xl font-bold text-center mb-8"
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ ...bounceTransition, delay: 0.2 }}
						>
							Automated{" "}
							<span className="relative">
								<span className="text-primary">Motorized Shades</span>
							</span>
						</motion.h1>

						<motion.div
							className="relative aspect-[21/9] rounded-3xl overflow-hidden shadow-2xl mb-12"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ ...bounceTransition, delay: 0.3 }}
						>
							<img
								src="/images/info/automation.png"
								alt="Automated Window Shades"
								className="w-full h-full object-cover"
							/>
							<div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />
						</motion.div>

						<motion.h2
							className="text-3xl text-center mb-8 text-gray-800"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ ...bounceTransition, delay: 0.4 }}
						>
							Off Cut Shades offers premium motors in our automated window
							shades
						</motion.h2>

						<motion.p
							className="text-xl text-gray-600 text-center max-w-4xl mx-auto mb-16"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ ...bounceTransition, delay: 0.5 }}
						>
							Transform your home into a smart haven with our cutting-edge
							motorized window shade automation systems. Effortlessly manage the
							amount of sunlight and privacy in your space with the tap of a
							button.
						</motion.p>
					</motion.div>
				</section>
				{/* Features Grid */}
				<section className="py-24 bg-gray-50">
					<div className="container mx-auto px-4">
						<div className="grid grid-cols-1 md:grid-cols-3 gap-8">
							{features.map((feature, index) => (
								<motion.div
									key={index}
									className="bg-white rounded-3xl p-8 shadow-lg"
									initial={{ opacity: 0, y: 50 }}
									whileInView={{ opacity: 1, y: 0 }}
									transition={{ ...bounceTransition, delay: index * 0.1 }}
									viewport={{ once: true }}
								>
									<div className="flex flex-col items-center text-center">
										<div className="mb-6">{feature.icon}</div>
										<h3 className="text-2xl font-bold mb-4">{feature.title}</h3>
										<p className="text-gray-600 mb-4">{feature.description}</p>
										{feature.note && (
											<p className="text-sm text-gray-500 italic">
												{feature.note}
											</p>
										)}
									</div>
								</motion.div>
							))}
						</div>
					</div>
				</section>
				{/* Benefits Section */}
				<section className="py-24">
					<div className="container mx-auto px-4">
						<motion.h2
							className="text-4xl font-bold text-center mb-16"
							initial={{ opacity: 0, y: 30 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={bounceTransition}
							viewport={{ once: true }}
						>
							Rechargeable Motor Features
						</motion.h2>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-12">
							{benefits.map((benefit, index) => (
								<motion.div
									key={index}
									className="flex gap-6"
									initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
									whileInView={{ opacity: 1, x: 0 }}
									transition={{ ...bounceTransition, delay: index * 0.1 }}
									viewport={{ once: true }}
								>
									<div className="flex-shrink-0 bg-primary/10 rounded-2xl p-4">
										{benefit.icon}
									</div>
									<div>
										<h3 className="text-2xl font-bold mb-2">{benefit.title}</h3>
										<p className="text-gray-600">{benefit.description}</p>
									</div>
								</motion.div>
							))}
						</div>
					</div>
				</section>
				{/* Connector Section */}
				<section className="py-24 bg-gray-50">
					<div className="container mx-auto px-4">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
							<motion.div
								className="text-center md:text-left"
								initial={{ opacity: 0, x: -50 }}
								whileInView={{ opacity: 1, x: 0 }}
								transition={bounceTransition}
								viewport={{ once: true }}
							>
								<div className="flex items-center gap-4 mb-6">
									<Wifi className="w-8 h-8 text-primary" />
									<h2 className="text-3xl font-bold">Connector Hub</h2>
								</div>
								<h3 className="text-2xl text-gray-700 mb-4">
									More Affordable Than Ever Before
								</h3>
								<p className="text-gray-600 text-lg">
									Use the Connector Hub enables WiFi based control via the Smart
									Life app for intuitive smart home automation.
								</p>
							</motion.div>

							<motion.div
								initial={{ opacity: 0, x: 50 }}
								whileInView={{ opacity: 1, x: 0 }}
								transition={bounceTransition}
								viewport={{ once: true }}
								className="flex justify-center"
							>
								<img
									src="https://mysunglow.com/wp-content/uploads/bridge-e1692369969595.png"
									alt="Connector Hub"
									className="max-w-sm w-full"
								/>
							</motion.div>
						</div>

						{/* Smart Home Integration */}
						<motion.div
							className="text-center mt-24"
							initial={{ opacity: 0, y: 30 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={bounceTransition}
							viewport={{ once: true }}
						>
							<h3 className="text-2xl font-bold mb-8">
								Compatible with Leading Smart Home Platforms
							</h3>
							<img
								src="https://mysunglow.com/wp-content/uploads/smarthome-logos-revised-1.png"
								alt="Smart Home Integration"
								className="max-w-4xl w-full mx-auto mb-8"
							/>
							<p className="text-gray-600">
								Download the Smart Life App from:{" "}
								<a
									href="https://play.google.com/store/apps/details?id=com.tuya.smartlife"
									className="text-primary hover:text-secondary"
									target="_blank"
									rel="noopener noreferrer"
								>
									Google Play Store
								</a>{" "}
								|{" "}
								<a
									href="https://apps.apple.com/us/app/smart-life-smart-living/id1115101477"
									className="text-primary hover:text-secondary"
									target="_blank"
									rel="noopener noreferrer"
								>
									App Store
								</a>
							</p>
						</motion.div>

						{/* Instructions on how to choose motor in customizer */}
						<motion.div
							className="text-center mt-24"
							initial={{ opacity: 0, y: 30 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={bounceTransition}
							viewport={{ once: true }}
						>
							<h3 className="text-2xl font-bold mb-8">
								How to Select Motorized Operation in the Customization
							</h3>
							<div className="text-gray-600 text-lg space-y-4 max-w-2xl mx-auto">
								<ul className="mx-6 list-disc text-left pl-8">
									<li>Go to our shade customizer</li>
									<li>Complete steps 1-3 for your shade specifications</li>
									<li>In step 4, you'll see "Select Control System" heading</li>
									<li>Select the "Automated" option for motorized operation</li>
									<li>Continue with the remaining customization steps</li>
								</ul>
							</div>
						</motion.div>
					</div>
				</section>
				{/* Contact Section */}
				<ContactBlock />
			</main>
		</div>
	);
};

export default MotorizedSmart;
